import { createColumnHelper } from '@tanstack/react-table'
import type { CatalogoConMetricas } from '@/hooks/useImpuestosPage'
import { ImpuestoBadge } from '@/components/ui/impuesto-badge'
import { Button } from '@/components/ui/button'
import { Eye, ArrowUpDown, ArrowUp, ArrowDown, Lock } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { CATEGORIAS_IMPUESTO_CATALOGO } from '@/types/impuestos'
import type { ColorImpuesto } from '@/types/impuestos'

const columnHelper = createColumnHelper<CatalogoConMetricas>()

export function createImpuestosColumns(
  onVerDetalle: (impuesto: CatalogoConMetricas) => void,
) {
  return [
    // 1. Impuesto: ImpuestoBadge + nombre
    columnHelper.accessor('nombre', {
      id: 'nombre',
      size: 200,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Impuesto
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => {
        const { nombre, nombre_corto, color, is_global } = row.original
        return (
          <div className="flex items-center gap-2">
            <ImpuestoBadge
              nombre={nombre_corto ?? nombre.slice(0, 4).toUpperCase()}
              color={(color as ColorImpuesto) ?? 'gray'}
              size="sm"
            />
            <span className="font-medium text-sm truncate">{nombre}</span>
            {is_global && <Lock className="h-3 w-3 text-muted-foreground shrink-0" />}
          </div>
        )
      },
    }),

    // 2. Categoria
    columnHelper.accessor('categoria', {
      id: 'categoria',
      size: 130,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Categoría
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => {
        const label = CATEGORIAS_IMPUESTO_CATALOGO[
          row.original.categoria as keyof typeof CATEGORIAS_IMPUESTO_CATALOGO
        ] ?? row.original.categoria
        return <span className="text-sm text-muted-foreground">{label}</span>
      },
    }),

    // 3. Clientes asignados
    columnHelper.accessor('clientes_asignados', {
      id: 'clientes_asignados',
      size: 90,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Clientes
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => (
        <span className="font-medium tabular-nums text-sm">
          {row.original.clientes_asignados}
        </span>
      ),
    }),

    // 4. Vencimientos pendientes
    columnHelper.accessor('vencimientos_pendientes', {
      id: 'vencimientos_pendientes',
      size: 100,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Pendientes
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => {
        const count = row.original.vencimientos_pendientes
        return (
          <span
            className={`tabular-nums text-sm ${
              count > 0 ? 'text-orange-600 dark:text-orange-400 font-medium' : 'text-muted-foreground'
            }`}
          >
            {count}
          </span>
        )
      },
    }),

    // 5. Vencimientos vencidos
    columnHelper.accessor('vencimientos_vencidos', {
      id: 'vencimientos_vencidos',
      size: 90,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Vencidos
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => {
        const count = row.original.vencimientos_vencidos
        return (
          <span
            className={`tabular-nums text-sm ${
              count > 0 ? 'text-red-600 dark:text-red-400 font-medium' : 'text-muted-foreground'
            }`}
          >
            {count}
          </span>
        )
      },
    }),

    // 6. Proximo vencimiento
    columnHelper.accessor('proximo_vencimiento', {
      id: 'proximo_vencimiento',
      size: 120,
      header: ({ column }) => {
        const isSorted = column.getIsSorted()
        return (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3 h-8 px-2 hover:bg-muted"
            onClick={() => column.toggleSorting()}
          >
            Prox. Vencimiento
            {isSorted === 'asc' && <ArrowUp className="ml-1 h-3 w-3" />}
            {isSorted === 'desc' && <ArrowDown className="ml-1 h-3 w-3" />}
            {!isSorted && <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
          </Button>
        )
      },
      cell: ({ row }) => {
        const fecha = row.original.proximo_vencimiento
        if (!fecha) return <span className="text-muted-foreground text-sm">-</span>
        return (
          <span className="text-sm tabular-nums">
            {format(parseISO(fecha), "d 'de' MMM", { locale: es })}
          </span>
        )
      },
    }),

    // 7. Acciones
    columnHelper.display({
      id: 'acciones',
      size: 60,
      enableSorting: false,
      header: () => null,
      cell: ({ row }) => {
        const impuesto = row.original
        return (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => onVerDetalle(impuesto)}
              title="Ver detalle"
            >
              <Eye className="h-3.5 w-3.5" />
            </Button>
          </div>
        )
      },
    }),
  ]
}
